import { apiRequest } from './client'
import type {
  ImportMarketplaceEntitlementBody,
  ImportMarketplaceEntitlementResult,
  SilkMarketplaceEntitlementList,
  SilkMarketplaceListing,
  SilkMarketplaceListingList,
  SilkMarketplaceListingSample,
  SilkMarketplaceOrder,
  SilkMarketplaceOrderList,
  SilkMarketplaceTradeFeedList,
} from './types'

export type MarketplaceListingType =
  | 'licensed_work'
  | 'script_project'
  | 'asset_bundle'
  | 'studio_script'
  | 'studio_asset'
  | 'studio_creative'
export type MarketplaceLicenseMode = 'non_exclusive' | 'exclusive'

/** 兼容历史 PascalCase 响应(后端 DTO 化前) */
function normalizeListing(raw: Record<string, unknown>): SilkMarketplaceListing {
  return {
    id: Number(raw.id ?? raw.ID ?? 0),
    public_id: String(raw.public_id ?? raw.PublicID ?? ''),
    source_tenant_id: Number(raw.source_tenant_id ?? raw.SourceTenantID ?? 0),
    seller_user_id: Number(raw.seller_user_id ?? raw.SellerUserID ?? 0),
    listing_type: String(raw.listing_type ?? raw.ListingType ?? 'licensed_work') as SilkMarketplaceListing['listing_type'],
    work_id: (raw.work_id ?? raw.WorkID ?? null) as number | null | undefined,
    title: String(raw.title ?? raw.Title ?? ''),
    summary: String(raw.summary ?? raw.Summary ?? ''),
    cover_url: String(raw.cover_url ?? raw.CoverURL ?? '') || undefined,
    samples: normalizeSamples(raw.samples ?? raw.Samples),
    price_points: Number(raw.price_points ?? raw.PricePoints ?? 0),
    license_mode: String(raw.license_mode ?? raw.LicenseMode ?? 'non_exclusive') as MarketplaceLicenseMode,
    status: String(raw.status ?? raw.Status ?? ''),
    reject_reason: String(raw.reject_reason ?? raw.RejectReason ?? '') || undefined,
    featured: Boolean(raw.featured ?? raw.Featured ?? false),
    sold_count: Number(raw.sold_count ?? raw.SoldCount ?? 0),
    published_at: String(raw.published_at ?? raw.PublishedAt ?? '') || undefined,
    created_at: String(raw.created_at ?? raw.CreatedAt ?? ''),
    updated_at: String(raw.updated_at ?? raw.UpdatedAt ?? ''),
  }
}

function normalizeSamples(raw: unknown): SilkMarketplaceListingSample[] {
  if (!Array.isArray(raw)) return []
  const out: SilkMarketplaceListingSample[] = []
  for (const item of raw) {
    const row = (item ?? {}) as Record<string, unknown>
    const url = String(row.url ?? row.URL ?? '').trim()
    if (!url) continue
    out.push({
      media_type: String(row.media_type ?? row.MediaType ?? 'image'),
      url,
      poster_url: String(row.poster_url ?? row.PosterURL ?? '') || undefined,
      title: String(row.title ?? row.Title ?? '') || undefined,
      sort_ord: Number(row.sort_ord ?? row.SortOrd ?? 0) || undefined,
    })
  }
  return out
}

function normalizeListingList(data: SilkMarketplaceListingList | Record<string, unknown>): SilkMarketplaceListingList {
  const raw = data as Record<string, unknown>
  const items = (raw.items ?? raw.Items ?? []) as Record<string, unknown>[]
  return {
    total: Number(raw.total ?? raw.Total ?? items.length),
    items: items.map(normalizeListing),
  }
}

export interface CreateMarketplaceListingBody {
  listing_type: MarketplaceListingType
  work_id?: number
  asset_ids?: number[]
  title: string
  summary?: string
  cover_url?: string
  price_points: number
  license_mode: MarketplaceLicenseMode
}

export interface UpdateMarketplaceListingBody {
  title?: string
  summary?: string
  cover_url?: string
  price_points?: number
  category_code?: string
}

export const marketplaceApi = {
  async catalog(params?: {
    type?: string
    category_code?: string
    limit?: number
    offset?: number
  }) {
    const data = await apiRequest<SilkMarketplaceListingList>('/api/v1/marketplace/listings', {
      query: params,
      auth: false,
    })
    return normalizeListingList(data)
  },

  async catalogGet(publicId: string) {
    const data = await apiRequest<SilkMarketplaceListing | Record<string, unknown>>(
      `/api/v1/marketplace/listings/${publicId}`,
      { auth: false },
    )
    return normalizeListing(data as Record<string, unknown>)
  },

  async recentTrades(params?: { limit?: number }) {
    return apiRequest<SilkMarketplaceTradeFeedList>('/api/v1/marketplace/trades/recent', {
      query: params,
      auth: false,
    })
  },

  async myListings(params?: { limit?: number; offset?: number }) {
    const data = await apiRequest<SilkMarketplaceListingList>('/api/v1/marketplace/listings/me', {
      query: params,
    })
    return normalizeListingList(data)
  },

  create(body: CreateMarketplaceListingBody) {
    return apiRequest<SilkMarketplaceListing>('/api/v1/marketplace/listings/me', {
      method: 'POST',
      body,
    }).then((row) => normalizeListing(row as unknown as Record<string, unknown>))
  },

  submit(id: number) {
    return apiRequest<SilkMarketplaceListing>(`/api/v1/marketplace/listings/${id}/submit`, {
      method: 'POST',
    }).then((row) => normalizeListing(row as unknown as Record<string, unknown>))
  },

  update(id: number, body: UpdateMarketplaceListingBody) {
    return apiRequest<SilkMarketplaceListing>(`/api/v1/marketplace/listings/${id}`, {
      method: 'PATCH',
      body,
    }).then((row) => normalizeListing(row as unknown as Record<string, unknown>))
  },

  remove(id: number) {
    return apiRequest<{ ok: boolean }>(`/api/v1/marketplace/listings/${id}`, {
      method: 'DELETE',
    })
  },

  offline(id: number) {
    return apiRequest<SilkMarketplaceListing>(`/api/v1/marketplace/listings/${id}/offline`, {
      method: 'POST',
    }).then((row) => normalizeListing(row as unknown as Record<string, unknown>))
  },

  purchase(publicId: string, idempotencyKey?: string) {
    return apiRequest<SilkMarketplaceOrder>(`/api/v1/marketplace/listings/${publicId}/purchase`, {
      method: 'POST',
      body: { idempotency_key: idempotencyKey ?? '' },
    })
  },

  myOrders(params?: { limit?: number; offset?: number }) {
    return apiRequest<SilkMarketplaceOrderList>('/api/v1/marketplace/orders/me', { query: params })
  },

  myEntitlements(params?: { limit?: number; offset?: number }) {
    return apiRequest<SilkMarketplaceEntitlementList>('/api/v1/marketplace/entitlements/me', {
      query: params,
    })
  },

  importEntitlement(id: number, body: ImportMarketplaceEntitlementBody) {
    return apiRequest<ImportMarketplaceEntitlementResult>(`/api/v1/marketplace/entitlements/${id}/import`, {
      method: 'POST',
      body,
    })
  },
}
